import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Mail, Calendar, ScrollText } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { ShareMenu } from '../components/ShareMenu';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { enhanceAccessibleHtml } from '../utils/a11yHtml';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const absoluteMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const u = url.trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = (BACKEND_URL || '').trim().replace(/\/+$/, '');
  if (!base) return u;
  return `${base}${u.startsWith('/') ? '' : '/'}${u}`;
};

const ResourcesPage = () => {
  usePageTitle('Resources');
  const [newsletters, setNewsletters] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [representations, setRepresentations] = useState([]);
  const [loadingNewsletters, setLoadingNewsletters] = useState(true);
  const [loadingUpdates, setLoadingUpdates] = useState(true);
  const [loadingRepresentations, setLoadingRepresentations] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/admin/newsletters/public/list`)
      .then((res) => setNewsletters(Array.isArray(res.data) ? res.data : []))
      .catch(() => setNewsletters([]))
      .finally(() => setLoadingNewsletters(false));
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/admin/regulatory-updates/public/list`)
      .then((res) => setUpdates(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUpdates([]))
      .finally(() => setLoadingUpdates(false));
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/admin/representations/public/list`)
      .then((res) => setRepresentations(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRepresentations([]))
      .finally(() => setLoadingRepresentations(false));
  }, []);

  const featuredNewsletter = newsletters.length > 0 && newsletters[0].is_featured ? newsletters[0] : null;
  const otherNewsletters = featuredNewsletter ? newsletters.slice(1) : newsletters;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-[#211F1F] mb-4">
              <span className="text-[#C8473A]">Resources</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl">
              Newsletters, regulatory updates, and policy representations for founders and partners.
            </p>
          </motion.div>

          {/* Newsletters */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-[#211F1F] mb-6 flex items-center gap-2">
              <Mail className="w-6 h-6 text-[#C8473A]" />
              Newsletters
            </h2>

            {loadingNewsletters ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 animate-pulse overflow-hidden">
                    <div className="h-40 bg-gray-100" />
                    <div className="p-5">
                      <div className="h-5 bg-gray-100 rounded w-3/4 mb-3" />
                      <div className="h-4 bg-gray-100 rounded w-full mb-2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : newsletters.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-500">
                No newsletters at this time. Check back later.
              </div>
            ) : (
              <>
                {featuredNewsletter && (
                  <div className="mb-8 relative">
                    <div className="absolute top-6 right-6 z-10" onClick={(e) => e.preventDefault()}>
                      <ShareMenu
                        url={typeof window !== 'undefined' ? `${window.location.origin}/resources/newsletters/${featuredNewsletter.newsletter_id}` : ''}
                        title={featuredNewsletter.title}
                        description={stripHtml(featuredNewsletter.content).slice(0, 200)}
                        triggerClassName="bg-white/90 hover:bg-white text-[#211F1F] border-0"
                      />
                    </div>
                    <Link to={`/resources/newsletters/${featuredNewsletter.newsletter_id}`} className="block group">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#211F1F] to-[#3d3a3a] p-8 md:p-10"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <Badge className="bg-[#C8473A] text-white hover:bg-[#C8473A]">Featured</Badge>
                          <span className="text-muted-on-dark text-sm">
                            {formatDate(featuredNewsletter.published_date)}
                          </span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-[#C8473A] transition-colors line-clamp-2">
                          {featuredNewsletter.title}
                        </h3>
                        {featuredNewsletter.content ? (
                          <div
                            className="prose prose-invert prose-sm max-w-none text-gray-300 mb-6 pointer-events-none max-h-20 overflow-hidden
                              [&_img]:hidden [&_table]:hidden [&_iframe]:hidden
                              [&_h1]:text-gray-200 [&_h2]:text-gray-200 [&_h3]:text-gray-200
                              [&_p]:text-gray-300 [&_li]:text-gray-300 [&_a]:text-gray-200
                            "
                            dangerouslySetInnerHTML={{
                              __html: enhanceAccessibleHtml(featuredNewsletter.content, {
                                pageTitle: featuredNewsletter.title,
                              }),
                            }}
                          />
                        ) : (
                          <p className="text-gray-300 line-clamp-2 mb-6">—</p>
                        )}
                        <span className="inline-flex items-center gap-2 text-white font-medium group-hover:text-[#C8473A] transition-colors">
                          Read newsletter
                          <ArrowLeft className="w-4 h-4 rotate-180" />
                        </span>
                      </motion.div>
                    </Link>
                  </div>
                )}

                {otherNewsletters.length > 0 && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {otherNewsletters.map((item, index) => (
                      <motion.div
                        key={item.newsletter_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + index * 0.05 }}
                        className="relative"
                      >
                        <div className="absolute top-3 right-3 z-10" onClick={(e) => e.preventDefault()}>
                          <ShareMenu
                            url={typeof window !== 'undefined' ? `${window.location.origin}/resources/newsletters/${item.newsletter_id}` : ''}
                            title={item.title}
                            description={stripHtml(item.content).slice(0, 120)}
                            triggerClassName="bg-white/90 hover:bg-white text-gray-700 border-0 shadow-sm"
                          />
                        </div>
                        <Link
                          to={`/resources/newsletters/${item.newsletter_id}`}
                          className="block h-full group"
                        >
                          <article className="bg-white rounded-2xl border border-gray-100 hover:border-[#C8473A]/20 hover:shadow-lg transition-all h-full flex flex-col overflow-hidden">
                            <div className="p-5 flex-1 flex flex-col">
                              <div className="flex items-center gap-2 mb-3">
                                <Calendar className="w-4 h-4 text-muted-light" />
                                <span className="text-sm text-gray-500">
                                  {formatDate(item.published_date)}
                                </span>
                              </div>
                              <h3 className="text-lg font-semibold text-[#211F1F] group-hover:text-[#C8473A] transition-colors mb-2 line-clamp-2">
                                {item.title}
                              </h3>
                              {item.content ? (
                                <div
                                  className="prose prose-sm max-w-none text-sm text-gray-500 flex-1 pointer-events-none max-h-16 overflow-hidden
                                    [&_img]:hidden [&_table]:hidden [&_iframe]:hidden
                                    [&_h1]:text-gray-700 [&_h2]:text-gray-700 [&_h3]:text-gray-700
                                    [&_p]:text-gray-500 [&_li]:text-gray-500 [&_a]:text-[#C8473A]
                                  "
                                  dangerouslySetInnerHTML={{
                                    __html: enhanceAccessibleHtml(item.content, { pageTitle: item.title }),
                                  }}
                                />
                              ) : (
                                <p className="text-sm text-gray-500 line-clamp-2 flex-1">—</p>
                              )}
                              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#C8473A] mt-4">
                                Read more
                                <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
                              </span>
                            </div>
                          </article>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}
          </motion.section>

          {/* Regulatory Updates */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold text-[#211F1F] mb-6 flex items-center gap-2">
              <FileText className="w-6 h-6 text-[#C8473A]" />
              Regulatory Updates
            </h2>
            {loadingUpdates ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 animate-pulse">
                <div className="h-6 bg-gray-100 rounded w-3/4 mb-4" />
                <div className="h-4 bg-gray-100 rounded w-full mb-2" />
              </div>
            ) : updates.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-500">
                No regulatory updates at this time. Check back later.
              </div>
            ) : (
              <div className="space-y-4">
                {updates.map((item, index) => (
                  <motion.div
                    key={item.update_id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 + index * 0.05 }}
                    className="relative"
                  >
                    <div className="absolute top-4 right-4 z-10" onClick={(e) => e.preventDefault()}>
                      <ShareMenu
                        url={typeof window !== 'undefined' ? `${window.location.origin}/resources/regulatory-updates/${item.update_id}` : ''}
                        title={item.title}
                        description={item.summary || stripHtml(item.content || '').slice(0, 160)}
                        triggerClassName="bg-white hover:bg-gray-50 border border-gray-200 shadow-sm"
                      />
                    </div>
                    <Link
                      to={`/resources/regulatory-updates/${item.update_id}`}
                      className="block bg-white rounded-2xl border border-gray-100 p-6 hover:border-[#C8473A]/20 transition-colors group pr-14"
                    >
                      <h3 className="text-lg font-semibold text-[#211F1F] mb-2 group-hover:text-[#C8473A] transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                        {item.summary || stripHtml(item.content || '').slice(0, 160)}…
                      </p>
                      <p className="text-xs text-muted-light mt-3">
                        {formatDate(item.published_date)}
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#C8473A] mt-4">
                        Read more
                        <ArrowLeft className="w-4 h-4 rotate-180" />
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.section>

          {/* Representations */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mt-16"
          >
            <h2 className="text-2xl font-bold text-[#211F1F] mb-6 flex items-center gap-2">
              <ScrollText className="w-6 h-6 text-[#C8473A]" />
              Representations
            </h2>
            {loadingRepresentations ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 animate-pulse overflow-hidden">
                    <div className="h-40 bg-gray-100" />
                    <div className="p-5">
                      <div className="h-5 bg-gray-100 rounded w-3/4 mb-3" />
                      <div className="h-4 bg-gray-100 rounded w-full mb-2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : representations.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-500">
                No representations at this time. Check back later.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {representations.map((item, index) => {
                  const excerpt =
                    (item.summary && item.summary.trim()) ||
                    stripHtml(item.content || '').slice(0, 160);
                  const coverSrc = item.cover_image_url ? absoluteMediaUrl(item.cover_image_url) : '';
                  return (
                    <motion.div
                      key={item.representation_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.28 + index * 0.05 }}
                      className="relative"
                    >
                      <div className="absolute top-3 right-3 z-10" onClick={(e) => e.preventDefault()}>
                        <ShareMenu
                          url={
                            typeof window !== 'undefined'
                              ? `${window.location.origin}/resources/representations/${item.representation_id}`
                              : ''
                          }
                          title={item.title}
                          description={excerpt}
                          triggerClassName="bg-white/90 hover:bg-white text-gray-700 border-0 shadow-sm"
                        />
                      </div>
                      <Link
                        to={`/resources/representations/${item.representation_id}`}
                        className="block h-full group"
                      >
                        <article className="bg-white rounded-2xl border border-gray-100 hover:border-[#C8473A]/20 hover:shadow-lg transition-all h-full flex flex-col overflow-hidden">
                          {coverSrc ? (
                            <div className="aspect-[16/10] bg-gray-100 overflow-hidden">
                              <img
                                src={coverSrc}
                                alt={item.title ? `Cover image for ${item.title}` : 'Resource cover image'}
                                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                              />
                            </div>
                          ) : null}
                          <div className="p-5 flex-1 flex flex-col">
                            <div className="flex items-center gap-2 mb-3">
                              <Calendar className="w-4 h-4 text-muted-light" />
                              <span className="text-sm text-gray-500">{formatDate(item.published_date)}</span>
                            </div>
                            <h3 className="text-lg font-semibold text-[#211F1F] group-hover:text-[#C8473A] transition-colors mb-2 line-clamp-3">
                              {item.title}
                            </h3>
                            {excerpt ? (
                              <p className="text-sm text-gray-500 flex-1 line-clamp-3">{excerpt}</p>
                            ) : (
                              <p className="text-sm text-gray-500 flex-1 line-clamp-2">—</p>
                            )}
                            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#C8473A] mt-4">
                              Read more
                              <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
                            </span>
                          </div>
                        </article>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ResourcesPage;
